import { useRef, useState } from "react";
import { loadImageFile, orientationOf, resizeToBlob } from "@/lib/imageCrop";
import { removeEventImageFile, uploadEventImage, type ImageSlot } from "@/services/events";

type Props = {
  value: string | null;
  onChange: (url: string | null, slot: ImageSlot | null) => void;
};

const MAX_FILE = 15 * 1024 * 1024;
const LABEL: Record<ImageSlot, string> = { square: "Cuadrada", horizontal: "Horizontal", vertical: "Vertical" };

/** Un solo espacio de foto: detecta si es cuadrada, horizontal o vertical y se adapta sola. */
export default function EventImageSlot({ value, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadedHere = useRef<Set<string>>(new Set());
  const [kind, setKind] = useState<ImageSlot | null>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = async (file: File | undefined) => {
    setError(null);
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) return setError("Usa una imagen JPG, PNG o WEBP.");
    if (file.size > MAX_FILE) return setError("La imagen pesa más de 15 MB. Elige una más ligera.");
    setBusy(true);
    try {
      const { img, url: objectUrl } = await loadImageFile(file);
      try {
        if (img.naturalWidth < 200 || img.naturalHeight < 200) throw new Error("La imagen es muy pequeña (mínimo 200 px por lado).");
        const o = orientationOf(img.naturalWidth, img.naturalHeight);
        const slot: ImageSlot = o === "cuadrada" ? "square" : o;
        const blob = await resizeToBlob(img, 1600);
        const url = await uploadEventImage(slot, blob);
        uploadedHere.current.add(url);
        if (value && uploadedHere.current.has(value)) await removeEventImageFile(value);
        setKind(slot);
        setSize({ w: img.naturalWidth, h: img.naturalHeight });
        onChange(url, slot);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    } catch (e) {
      setError(e instanceof Error && e.message.startsWith("La imagen") ? e.message : "No se pudo subir la imagen. Intenta de nuevo.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (value && uploadedHere.current.has(value)) await removeEventImageFile(value);
    setKind(null);
    setSize(null);
    onChange(null, null);
  };

  return (
    <div className="rounded-xl border border-background-200 bg-background-50 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-foreground-800">Foto del evento <span className="font-normal text-foreground-500">(opcional)</span></p>
          <p className="text-xs text-foreground-500 mt-0.5">Mejor cuadrada; también sirve horizontal o vertical. Se adapta sola en el calendario, la lista y el detalle.</p>
        </div>
        {value && !busy && (
          <div className="flex items-center gap-1.5 shrink-0">
            <button type="button" onClick={() => inputRef.current?.click()} className="px-3 py-1.5 rounded-lg bg-white border border-background-200 text-xs font-semibold text-foreground-700 hover:border-primary-400 cursor-pointer whitespace-nowrap">
              <i className="ri-refresh-line mr-1" />Cambiar
            </button>
            <button type="button" onClick={remove} className="px-2.5 py-1.5 rounded-lg bg-white border border-background-200 text-xs font-semibold text-red-600 hover:border-red-300 cursor-pointer" aria-label="Quitar foto">
              <i className="ri-delete-bin-line" />
            </button>
          </div>
        )}
      </div>

      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { void pick(e.target.files?.[0]); e.target.value = ""; }} />

      {busy ? (
        <div className="mt-3 flex items-center justify-center gap-2 rounded-lg border border-dashed border-background-300 py-6 text-xs text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-lg" /> Subiendo y optimizando…
        </div>
      ) : value ? (
        <div className="mt-3 flex items-center gap-3">
          <div className="rounded-lg bg-background-200 overflow-hidden flex items-center justify-center max-w-[11rem] max-h-44">
            <img src={value} alt="Foto del evento" className="block max-w-full max-h-44 object-contain" />
          </div>
          <p className="text-xs text-foreground-500">
            {kind ? (
              <>
                Detectada: <span className="font-semibold text-foreground-700">{LABEL[kind]}</span>
                {size ? ` · ${size.w}×${size.h}` : ""}
              </>
            ) : (
              "Foto cargada."
            )}
          </p>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} className="mt-3 w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-background-300 py-5 text-xs text-foreground-500 hover:border-primary-400 cursor-pointer">
          <i className="ri-image-add-line text-lg" /> Subir foto — JPG, PNG o WEBP
        </button>
      )}
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  );
}
